import type {
  MappingJobNotification,
  MappingJobWebSocketMessage,
} from "@models/job";
import { buildMappingJobNotification } from "@utils/builders/job";
import { getAccessTokenSync } from "@utils/tokenStore";

const BASE_URL = import.meta.env.VITE_MAPPING_NOTIFICATION_URL;

/**
 * Org-broadcast WebSocket client for the xplatform mapping service.
 *
 * Unlike the extraction frontend's per-process socket, the mapping
 * backend pushes `mapping.job.{completed,failed,progress}` events for
 * every job in the caller's org over a single connection. The client opens one
 * socket per browser session, authenticates with the BeeHive JWT, and
 * dispatches incoming messages to per-job subscribers.
 *
 * Wire contract — see xplatform_mapping_service/docs/Flows.md#client-subscribe-loop.
 */

type SubscriberCallback = (message: MappingJobNotification) => void;

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // API GW idle timeout is 10min

class NotificationApi {
  private websocket: WebSocket | null = null;
  private isAuthenticated = false;
  private heartbeatInterval: number | null = null;
  private connectPromise: Promise<void> | null = null;
  private subscribers: Map<string, Set<SubscriberCallback>> = new Map();

  /**
   * The socket needs a token synchronously here and on every reconnect, so
   * it reads the in-memory store rather than awaiting BeeHive. The auth
   * bridge fills the store before any component that opens a socket mounts.
   */
  private getAccessToken(): string | null {
    return getAccessTokenSync();
  }

  /**
   * Open the WS and authenticate. Idempotent — repeated calls return the
   * same in-flight promise. Resolves once the auth frame has been sent.
   */
  public connect(): Promise<void> {
    if (this.isAuthenticated && this.websocket?.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }
    if (this.connectPromise) {
      return this.connectPromise;
    }
    if (!BASE_URL) {
      return Promise.reject(
        new Error(
          "VITE_MAPPING_NOTIFICATION_URL is not set. Cannot open job status WebSocket.",
        ),
      );
    }
    const token = this.getAccessToken();
    if (!token) {
      return Promise.reject(
        new Error("No access token available for WebSocket authentication."),
      );
    }

    this.connectPromise = new Promise<void>((resolve, reject) => {
      const socket = new WebSocket(BASE_URL);
      this.websocket = socket;

      socket.onopen = () => {
        socket.send(JSON.stringify({ type: "authenticate", token }));
        this.isAuthenticated = true;
        this.startHeartbeat();
        resolve();
      };

      socket.onmessage = (event) => {
        this.handleMessage(event);
      };

      socket.onerror = (error) => {
        console.error("Mapping notification WebSocket error:", error);
        if (!this.isAuthenticated) {
          reject(new Error("WebSocket connection error during handshake."));
        }
      };

      socket.onclose = () => {
        this.cleanup();
      };
    }).finally(() => {
      this.connectPromise = null;
    });

    return this.connectPromise;
  }

  /**
   * Subscribe to events for a specific job_id. Returns an unsubscribe fn.
   * Multiple subscribers per job are allowed (e.g. list view + detail view).
   */
  public subscribe(jobId: string, callback: SubscriberCallback): () => void {
    const set = this.subscribers.get(jobId) ?? new Set<SubscriberCallback>();
    set.add(callback);
    this.subscribers.set(jobId, set);
    return () => {
      const current = this.subscribers.get(jobId);
      if (!current) return;
      current.delete(callback);
      if (current.size === 0) {
        this.subscribers.delete(jobId);
      }
    };
  }

  private handleMessage(event: MessageEvent): void {
    let parsed: MappingJobWebSocketMessage;
    try {
      parsed = JSON.parse(event.data) as MappingJobWebSocketMessage;
    } catch (error) {
      console.error("Failed to parse WebSocket message:", error, event.data);
      return;
    }
    if (
      parsed.type !== "mapping.job.completed" &&
      parsed.type !== "mapping.job.failed" &&
      parsed.type !== "mapping.job.progress"
    ) {
      return;
    }
    const notification = buildMappingJobNotification(parsed);
    const callbacks = this.subscribers.get(notification.jobId);
    if (!callbacks) return;
    for (const callback of callbacks) {
      try {
        callback(notification);
      } catch (error) {
        console.error("Subscriber callback threw:", error);
      }
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatInterval = window.setInterval(() => {
      if (this.websocket?.readyState === WebSocket.OPEN) {
        this.websocket.send(JSON.stringify({ type: "ping" }));
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval !== null) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private cleanup(): void {
    this.stopHeartbeat();
    this.isAuthenticated = false;
    this.websocket = null;
  }

  public disconnect(): void {
    this.subscribers.clear();
    this.stopHeartbeat();
    const socket = this.websocket;
    this.websocket = null;
    this.isAuthenticated = false;
    if (socket && socket.readyState < WebSocket.CLOSING) {
      socket.close();
    }
  }
}

export const notificationApi = new NotificationApi();
